import { Controller } from "@hotwired/stimulus"
import { isValidEmailStrict } from "../../utils/validation"

export default class extends Controller {
  static targets = ["sendTo", "friendList", "searchInput", "emailPrompt"]

  connect() {
    this.selectedRecipients = new Set()
    this.friends = []
    this.blockedEmails = new Set()
    this.emails = new Set()
    this.searchQuery = ""
    this.loadFriends()
  }

  loadFriends() {
    fetch("/friends.json")
      .then(response => response.json())
      .then(data => {
        this.friends = data.friends || []
        this.blockedEmails = new Set((data.blocked_emails || []).map(e => e.toLowerCase()))
        this.renderFriendList()
      })
      .catch(error => {
        console.error('Error loading friends:', error)
      })
  }

  handleSearch(event) {
    this.searchQuery = event.target.value.toLowerCase()
    const input = event.target.value.trim()

    if (this.hasEmailPromptTarget) {
      if (input && isValidEmailStrict(input)) {
        const emailLower = input.toLowerCase()
        if (this.blockedEmails.has(emailLower)) {
          this.hideEmailPrompt()
        } else {
          this.showEmailPrompt(input)
        }
      } else {
        this.hideEmailPrompt()
      }
    }

    this.renderFriendList()
  }

  handleEnter(event) {
    event.preventDefault()
    const input = event.target.value.trim()

    if (isValidEmailStrict(input)) {
      this.addEmail(input)
    }
  }

  addEmail(email) {
    this.emails.add(email)
    this.selectedRecipients.add(email)
    this.searchInputTarget.value = ""
    this.searchQuery = ""
    this.hideEmailPrompt()
    this.renderFriendList()
  }

  addEmailFromPrompt(event) {
    event.preventDefault()
    const email = event.currentTarget.dataset.email
    this.addEmail(email)
  }

  showEmailPrompt(email) {
    this.emailPromptTarget.innerHTML = `
      <div class="card has-p-3 has-mb-3">
        <div class="is-flex has-items-center has-justify-between">
          <div class="has-text-left">
            <strong>Add ${email} as a friend</strong>
            <br>
            <small class="has-text-meta">They'll receive an invitation to join</small>
          </div>
          <button type="button" class="button circle" data-email="${email}" data-action="click->share-again#addEmailFromPrompt">
            <i class="fa-solid fa-plus"></i>
          </button>
        </div>
      </div>
    `
    this.emailPromptTarget.classList.remove('is-hidden')
  }

  hideEmailPrompt() {
    if (this.hasEmailPromptTarget) {
      this.emailPromptTarget.classList.add('is-hidden')
      this.emailPromptTarget.innerHTML = ''
    }
  }

  renderFriendList() {
    if (!this.hasFriendListTarget) return

    let html = ''

    // Render added emails first
    this.emails.forEach(email => {
      html += this.recipientCard({ type: 'email', value: email, label: email })
    })

    // Filter and render friends based on search
    const filteredFriends = this.friends.filter(friend => {
      if (!this.searchQuery) return true
      return friend.label.toLowerCase().includes(this.searchQuery) ||
             friend.email.toLowerCase().includes(this.searchQuery)
    })

    filteredFriends.forEach(friend => {
      html += this.recipientCard({ type: 'friend', value: friend.value.toString(), label: friend.label, email: friend.email })
    })

    this.friendListTarget.innerHTML = html
  }

  recipientCard(recipient) {
    const isSelected = this.selectedRecipients.has(recipient.value)
    const initial = recipient.label.charAt(0).toUpperCase()
    const checkmark = isSelected ? `<i class="fa-solid fa-check fa-2x" style="color: var(--siimple-color-primary);"></i>` : ''
    const selectedClass = isSelected ? 'is-selected' : ''

    return `
      <div class="has-mb-3">
        <div class="friend card is-shadowed ${selectedClass} has-p-3 is-flex has-items-center has-justify-between"
             style="cursor: pointer;"
             data-recipient-type="${recipient.type}"
             data-recipient-value="${recipient.value}"
             data-action="click->share-again#toggleRecipient">
          <div class="avatar has-mr-2">${initial}</div>
          <div class="has-ml-2 is-flex-grow has-text-left">
            <strong>${recipient.label}</strong>
            ${recipient.email && recipient.type === 'friend' ? `<br><small class="has-text-meta">${recipient.email}</small>` : ''}
          </div>
          <div class="has-ml-auto">
            ${checkmark}
          </div>
        </div>
      </div>
    `
  }

  toggleRecipient(event) {
    const card = event.currentTarget.closest('[data-recipient-value]')
    const value = card.dataset.recipientValue

    if (this.selectedRecipients.has(value)) {
      this.selectedRecipients.delete(value)
    } else {
      this.selectedRecipients.add(value)
    }

    this.renderFriendList()
  }

  validateForm(event) {
    // Populate the hidden select with selected recipients before submitting
    const selectedArray = Array.from(this.selectedRecipients)
    const select = this.sendToTarget

    // Clear existing options
    select.innerHTML = ''

    // Add selected recipients as options
    selectedArray.forEach(value => {
      const option = document.createElement('option')
      option.value = value
      option.selected = true
      select.appendChild(option)
    })

    return true
  }
}
